import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import SyncIcon from "@mui/icons-material/Sync";
import { DataGrid, ptBR } from "@mui/x-data-grid";
import {
  fetchAllPackageVacancies,
  getPackageVacancies,
  syncDutyById,
  updateVacancyAdvances,
} from "~/services/medicalcompany/dutys";
import { sendToAntecipe } from "~/services/medicalcompany/advanceRequest";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { usePreferences } from "~/features/preferences/PreferencesProvider";
import {
  captureErrorMessage,
  captureMomentOf,
  isFixableCaptureError,
  RefusalDialog,
  SlaJustifyDialog,
  slaRequirementOf,
} from "~/features/feedback";
import { expiredInfoOf, expiredMessage, isExpiredRow } from "~/features/packages";
import { useFeatures } from "~/hooks/useFeatures";
import VacancyCards from "~/features/conference/VacancyCards";
import { getApiErrorCode, getApiErrorMessage, getMergedIntoId, isCanceledRequest } from "~/utils/apiError";
import { brl, formatIsoDateBR, joinIdsPt, plural } from "~/utils/format";
import { formatDateInHoursAndMinutes } from "~/utils/date";
import {
  applyItems,
  BULK_ACTIONS,
  buildBulkItems,
  isNotAdvancing,
  isNotValidated,
  isTotalRefusal,
  refusedRows,
  situationOf,
  SITUATION_LABEL,
  summarize,
  warnsNotValidated,
} from "./vacancyState";
import { FlagCell, PaymentDateCell, PaymentRuleCell, RealizedCell } from "./cells";
import SendPackageDialog from "./SendPackageDialog";
import MergedPackageDialog from "./MergedPackageDialog";

export const VACANCIES_PAGE_SIZE = 50;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const HOUR = /^([01]?\d|2[0-3]):[0-5]\d$/;
const hourOf = (value) => (value ? formatDateInHoursAndMinutes(new Date(value)) : "");
const SAVE_MERGE_CODES = ["PACKAGE_NOT_EDITABLE", "PACKAGE_MERGED"];
const SEND_MERGE_CODES = ["PACKAGE_MERGED"];

// Ruling P12: único ponto que reconhece "este pacote foi agrupado" num erro da API.
const mergedTargetOf = (error, codes) => (codes.includes(getApiErrorCode(error)) ? getMergedIntoId(error) : null);

const CLOSED_SEND = { open: false, rows: [], refusal: null, refusalLabel: null, carry: {} };
// Corpo do complete: só os campos preenchidos; vazio = PUT sem corpo (contrato antigo).
const sendBody = (body) => (Object.keys(body).length ? body : undefined);

export default function CompanyPackageReview({ packageId, packageRow, onClose, onSent, onOpenPackage }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [pendingIds, setPendingIds] = useState([]);
  const [selection, setSelection] = useState([]);
  const [bulkDate, setBulkDate] = useState("");
  const [sendState, setSendState] = useState(CLOSED_SEND);
  const [sending, setSending] = useState(false);
  // Janela (b) "Não antecipar": { rows, carry, error? }. Janela (c) da empresa: { requirement, body, rows, error? }.
  const [refusalState, setRefusalState] = useState(null);
  const [slaState, setSlaState] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [mergedInto, setMergedInto] = useState(null);
  // F1b: pacote expirado (na abertura pela linha, ou pelo 409 PACKAGE_NOT_EDITABLE/expired) fica só leitura.
  const [expiredAt, setExpiredAt] = useState(() => (isExpiredRow(packageRow) ? packageRow.expired_at || "" : null));

  const abortRef = useRef(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  const notifyUi = useNotify();
  const { features } = useFeatures();
  const locked = Boolean(features.packageExpiration) && expiredAt !== null;
  const { prefs, setPref } = usePreferences();
  const cards = prefs.conferenceDetail === "cards";
  const notify = (type, message) => notifyUi({ severity: type, message });

  const load = useCallback(
    async ({ silent = false } = {}) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (!silent) setLoading(true);
      try {
        const result = await getPackageVacancies(
          packageId,
          { limit: VACANCIES_PAGE_SIZE, offset: page * VACANCIES_PAGE_SIZE },
          { signal: controller.signal }
        );
        setRows(result.rows);
        setTotal(result.total);
        setLoadError(null);
      } catch (e) {
        if (isCanceledRequest(e)) return;
        setLoadError(getApiErrorMessage(e, "Não foi possível carregar os plantões deste pacote."));
      } finally {
        if (abortRef.current === controller) setLoading(false);
      }
    },
    [packageId, page]
  );

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  // 409 expired com a chave ligada: vira o banner fixo (antes do modal de agrupado, que usa o mesmo código).
  const takeExpired = (e) => {
    const info = features.packageExpiration ? expiredInfoOf(e) : null;
    if (info) setExpiredAt(info.expiredAt);
    return Boolean(info);
  };

  const handleError = (e, fallback) => {
    if (takeExpired(e)) return;
    const merged = mergedTargetOf(e, SAVE_MERGE_CODES);
    if (merged) {
      setMergedInto(merged);
      return;
    }
    notify("error", getApiErrorMessage(e, fallback));
  };

  const saveItems = async (items, successMessage) => {
    const ids = items.map((item) => item.id);
    const before = new Map(rowsRef.current.filter((r) => ids.includes(r.id)).map((r) => [r.id, r]));
    setRows((current) => applyItems(current, items));
    setPendingIds((current) => [...current, ...ids]);
    try {
      await updateVacancyAdvances(items);
      notify("success", successMessage);
      await load({ silent: true });
      return true;
    } catch (e) {
      setRows((current) => current.map((r) => before.get(r.id) || r));
      handleError(e, "Não foi possível salvar. Nada foi alterado.");
      return false;
    } finally {
      setPendingIds((current) => current.filter((id) => !ids.includes(id)));
    }
  };

  const setDraft = (rowId, field, value) => setDrafts((d) => ({ ...d, [`${rowId}.${field}`]: value }));
  const clearDraft = (rowId, field) =>
    setDrafts((d) => {
      const next = { ...d };
      delete next[`${rowId}.${field}`];
      return next;
    });

  const commitDraft = async (rowId, field) => {
    const value = draftsRef.current[`${rowId}.${field}`];
    if (value === undefined) return;
    const row = rowsRef.current.find((r) => r.id === rowId);
    const current = field === "normal_payment_date" ? (row?.normal_payment_date || "").slice(0, 10) : row?.[field] || "";
    if (value === current) {
      clearDraft(rowId, field);
      return;
    }
    const isDate = field === "normal_payment_date";
    // Hora vazia cai aqui como inválida: o batch do AB não apaga horário (nunca mandamos null).
    if (!(isDate ? ISO_DATE.test(value) : HOUR.test(value))) {
      notify("error", isDate ? "Data inválida. Use o seletor de data." : "Hora inválida. Use o formato HH:MM.");
      return;
    }
    const ok = await saveItems([{ id: rowId, [field]: value }], isDate ? "Data de pagamento normal salva." : "Horário salvo.");
    if (ok) clearDraft(rowId, field);
  };

  const toggleFlag = (rowId, field, checked) => saveItems([{ id: rowId, [field]: checked }], "Plantão atualizado.");
  const changeRule = (rowId, value) => saveItems([{ id: rowId, payment_rule: value }], "Regra de pagamento atualizada.");
  const syncRow = async (rowId) => {
    if (locked) return;
    try {
      await syncDutyById(rowId);
      notify("success", "Plantão sincronizado com o Minha Escala.");
      await load({ silent: true });
    } catch (e) {
      handleError(e, "Não foi possível sincronizar o plantão.");
    }
  };

  const handlersRef = useRef({});
  handlersRef.current = { setDraft, commitDraft, toggleFlag, changeRule, syncRow };

  const selectedRows = rows.filter((r) => selection.includes(r.id));

  const applyDate = () =>
    saveItems(
      buildBulkItems(selection, { normal_payment_date: bulkDate }),
      `Data de pagamento normal ${formatIsoDateBR(bulkDate)} aplicada a ${plural(selection.length, "plantão", "plantões")}.`
    );

  const conferAndRelease = () => {
    const nv = selectedRows.filter(isNotValidated).length;
    const noDate = selectedRows.some((r) => !r.normal_payment_date);
    const message =
      `${plural(selection.length, "plantão conferido e liberado", "plantões conferidos e liberados")}.` +
      (nv
        ? ` ${plural(nv, "com valor não validado não será pago", "com valor não validado não serão pagos")} até a revisão do valor.`
        : "") +
      (noDate ? " Falta a data de pagamento em alguns." : "");
    return saveItems(buildBulkItems(selection, BULK_ACTIONS.conferAndRelease), message);
  };

  const notAdvance = async () => {
    const ids = [...selection];
    const ok = await saveItems(
      buildBulkItems(ids, BULK_ACTIONS.notAdvance),
      `${plural(ids.length, "plantão marcado", "plantões marcados")} para não antecipar agora.`
    );
    if (ok) setSelection((current) => current.filter((id) => !ids.includes(id)));
  };

  const openSend = async () => {
    setSendError(null);
    try {
      // D11: pacote maior que a página carregada → busca todos antes do aviso de divisão.
      const all = total > rows.length ? await fetchAllPackageVacancies(packageId) : rows;
      // Janela (b) proativa: com plantão "Não antecipar", o motivo vem antes do envio.
      if (features.feedbackCapture && all.some(isNotAdvancing)) setRefusalState({ rows: all, carry: {} });
      else setSendState({ ...CLOSED_SEND, open: true, rows: all });
    } catch (e) {
      handleError(e, "Não foi possível conferir os plantões antes do envio.");
    }
  };

  const closeWindows = () => {
    setSendState(CLOSED_SEND);
    setRefusalState(null);
    setSlaState(null);
  };

  // 422 de captura: abre a janela pedida (cada uma entra no corpo uma vez por envio) ou mostra o erro.
  const routeSendError = (e, body, all) => {
    // 409 expired com a chave packageExpiration: banner fixo e painel só leitura (antes do modal de agrupado, que usa o mesmo código).
    if (takeExpired(e)) {
      closeWindows();
      return;
    }
    const requirement = slaRequirementOf(e);
    if (requirement && !body.sla) {
      closeWindows();
      setSlaState({ requirement, body, rows: all });
      return;
    }
    const code = getApiErrorCode(e);
    const moment = captureMomentOf(e);
    const message = captureErrorMessage(e, "Tente de novo em instantes.");
    if (code === "REASON_REQUIRED" && (moment === "refusal" || moment === null) && !body.refusal) {
      closeWindows();
      setRefusalState({ rows: all, carry: body });
      return;
    }
    if (isFixableCaptureError(e) && moment === "refusal" && body.refusal) {
      const { refusal, ...carry } = body;
      closeWindows();
      setRefusalState({ rows: all, carry, error: message });
      return;
    }
    if (isFixableCaptureError(e) && moment === "sla_breach" && body.sla && slaState) {
      const { sla, ...rest } = body;
      setSlaState({ ...slaState, body: rest, error: message });
      return;
    }
    closeWindows();
    const merged = mergedTargetOf(e, SEND_MERGE_CODES);
    if (merged) {
      setMergedInto(merged);
      return;
    }
    // Inclui o 409 PACKAGE_NOT_EDITABLE (pacote mudou enquanto a janela estava aberta): avisa e recarrega.
    setSendError(message);
    load({ silent: true });
  };

  const doSend = async (body, all) => {
    const splitCount = all.filter(isNotAdvancing).length;
    setSending(true);
    try {
      const response = await sendToAntecipe(packageId, sendBody(body));
      const payload = response?.data || {};
      // B1a: recusa total devolve o pacote já `canceled`, sem filho.
      const refusedAll = (payload.data?.status ?? payload.status) === "canceled";
      setSending(false);
      closeWindows();
      // AB D18: número do filho do split e do resultante do automático (null em AB antigo)
      onSent({
        packageId,
        splitCount: refusedAll ? 0 : splitCount,
        splitChildId: payload.splitChildAdvancePaymentId ?? null,
        autoMergedIntoId: payload.autoMergedIntoAdvancePaymentId ?? null,
        refusedAll,
      });
    } catch (e) {
      setSending(false);
      routeSendError(e, body, all);
    }
  };

  const confirmSend = () => doSend({ ...sendState.carry, ...(sendState.refusal ? { refusal: sendState.refusal } : {}) }, sendState.rows);

  const confirmRefusal = (refusal, label) => {
    const { rows: all, carry } = refusalState;
    // Recusa total: envia direto (o pacote fecha como recusado). Parcial: segue para o resumo do envio.
    if (isTotalRefusal(all)) doSend({ ...carry, refusal }, all);
    else {
      setRefusalState(null);
      setSendState({ open: true, rows: all, refusal, refusalLabel: label, carry });
    }
  };

  const packageSummary = (all) =>
    [
      `Pacote #${packageId}`,
      packageRow?.doctor_name,
      plural(all.length, "plantão", "plantões"),
      brl(all.reduce((acc, r) => acc + Number(r.real_net_value || 0), 0)),
    ]
      .filter(Boolean)
      .join(" · ");

  const columns = useMemo(() => {
    const h = () => handlersRef.current;
    const busy = (id) => locked || pendingIds.includes(id);
    const onDraft = (...a) => h().setDraft(...a);
    const onCommit = (...a) => h().commitDraft(...a);
    return [
      { field: "id", headerName: "Plantão", width: 90, sortable: false, valueFormatter: ({ value }) => `#${value}` },
      { field: "hospital_name", headerName: "Unidade", width: 200, sortable: false },
      { field: "date", headerName: "Data", width: 105, sortable: false, valueFormatter: ({ value }) => formatIsoDateBR(value) },
      {
        field: "planned",
        headerName: "Previsto",
        width: 120,
        sortable: false,
        valueGetter: ({ row }) => `${hourOf(row.start_hour)} – ${hourOf(row.end_hour)}`,
      },
      {
        field: "realized",
        headerName: "Realizado",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <RealizedCell row={row} drafts={drafts} disabled={busy(row.id)} onDraft={onDraft} onCommit={onCommit} />
        ),
      },
      {
        field: "gross_value",
        headerName: "Valor bruto",
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (isNotValidated(row) ? <span className="warnchip">⚠ Valor não validado</span> : brl(row.gross_value)),
      },
      {
        field: "real_net_value",
        headerName: "Valor proporcional",
        width: 150,
        sortable: false,
        valueFormatter: ({ value }) => (value == null ? "—" : brl(value)),
      },
      {
        field: "normal_payment_date",
        headerName: "Pagamento normal",
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <PaymentDateCell
            rowId={row.id}
            value={row.normal_payment_date}
            draft={drafts[`${row.id}.normal_payment_date`]}
            disabled={busy(row.id)}
            onDraft={onDraft}
            onCommit={onCommit}
          />
        ),
      },
      ...[
        ["verified_documents", "Docs", "Documentos verificados"],
        ["can_advance", "Pode adiantar", "Pode adiantar"],
        ["is_checked", "Revisado", "Revisado"],
      ].map(([field, headerName, label]) => ({
        field,
        headerName,
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <FlagCell rowId={row.id} field={field} label={label} value={row[field]} disabled={busy(row.id)} onToggle={(...a) => h().toggleFlag(...a)} />
        ),
      })),
      {
        field: "situation",
        headerName: "Situação",
        width: 190,
        sortable: false,
        valueGetter: ({ row }) => SITUATION_LABEL[situationOf(row)],
      },
      {
        field: "payment_rule",
        headerName: "Regra de pagamento",
        width: 230,
        sortable: false,
        renderCell: ({ row }) => (
          <PaymentRuleCell rowId={row.id} value={row.payment_rule} disabled={busy(row.id)} onChange={(...a) => h().changeRule(...a)} />
        ),
      },
      {
        field: "sync",
        headerName: "",
        width: 60,
        sortable: false,
        renderCell: ({ row }) => (
          <IconButton aria-label={`Sincronizar plantão ${row.id}`} disabled={locked} onClick={() => h().syncRow(row.id)}>
            <SyncIcon />
          </IconButton>
        ),
      },
    ];
  }, [drafts, pendingIds, locked]);

  const nvRows = rows.filter(warnsNotValidated);
  const summary = summarize(rows);
  const hasSelection = selection.length > 0;
  const canBulk = hasSelection && !locked;
  // Ruling P14 (D11): pacote maior que a página → a contagem vale só para a página carregada.
  const pagedPackage = total > rows.length;

  return (
    <Box component="section" aria-label={`Conferir plantões do pacote ${packageId}`} sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography component="h3" variant="h6">
          Conferir plantões · Pacote #{packageId}
        </Typography>
        {packageRow && (
          <Typography variant="body2" color="text.secondary">
            {packageRow.doctor_name} · CRM {packageRow.doctor_crm}/{packageRow.doctor_crm_uf} · {packageRow.company_name} ·{" "}
            {plural(total, "plantão", "plantões")}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={prefs.conferenceDetail}
          onChange={(_, value) => value && setPref("conferenceDetail", value)}
          aria-label="Exibição dos plantões"
        >
          <ToggleButton value="table" sx={{ textTransform: "none", px: 1.5 }}>
            Tabela
          </ToggleButton>
          <ToggleButton value="cards" sx={{ textTransform: "none", px: 1.5 }}>
            Cartões
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {locked && (
        <Alert severity="warning" data-testid="expired-banner" sx={{ mb: 1 }}>
          {expiredAt ? expiredMessage(expiredAt) : "Pacote expirado; peça a reabertura à Operação."}
        </Alert>
      )}
      {sendError && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => setSendError(null)}>
              Entendi
            </Button>
          }
        >
          <b>Não foi possível enviar o pacote #{packageId}.</b> {sendError}
          <Box sx={{ fontSize: 12, opacity: 0.8 }}>Nada foi alterado. O que você preencheu continua na tela.</Box>
        </Alert>
      )}
      {nvRows.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <b>{plural(nvRows.length, "plantão está", "plantões estão")} com valor não validado</b> ({joinIdsPt(nvRows.map((r) => r.id))}). O valor bruto
          chegou zerado do Minha Escala. {nvRows.length === 1 ? "Ele não será pago" : "Eles não serão pagos"} até o valor ser revisado. Você pode
          marcá-los como “Não antecipar” e seguir com os outros.
        </Alert>
      )}
      {loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Tentar de novo
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : (
        <>
          <Box
            role="toolbar"
            aria-label="Ações em massa"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              p: 1,
              mb: 1,
              borderRadius: 2,
              bgcolor: hasSelection ? "#e8f1f5" : "#f5f7fa",
              border: "1px solid",
              borderColor: hasSelection ? "#c5dbe5" : "#eef0f3",
              color: hasSelection ? "primary.main" : "text.secondary",
            }}
          >
            {hasSelection ? (
              <span>
                <b>{selection.length}</b> selecionados
              </span>
            ) : (
              <span>Marque plantões para aplicar ações a vários de uma vez.</span>
            )}
            <TextField
              type="date"
              size="small"
              label="Data de pagamento normal"
              InputLabelProps={{ shrink: true }}
              value={bulkDate}
              onChange={(e) => setBulkDate(e.target.value)}
              disabled={!canBulk}
              sx={{ bgcolor: "background.paper", borderRadius: 1 }}
            />
            <Button variant="outlined" disabled={!canBulk || !ISO_DATE.test(bulkDate)} onClick={applyDate}>
              Aplicar data a {selection.length} selecionados
            </Button>
            <Button variant="contained" disabled={!canBulk} onClick={conferAndRelease}>
              ✓ Conferir e liberar
            </Button>
            <Button variant="outlined" color="inherit" disabled={!canBulk} onClick={notAdvance}>
              Não antecipar
            </Button>
          </Box>
          {cards ? (
            <>
              {/* o DataGrid tem o próprio `loading`; nos cartões a lista só entra depois da 1ª carga */}
              {loading && <LinearProgress sx={{ mb: 1 }} />}
              {(!loading || rows.length > 0) && (
                <VacancyCards
                  packageId={packageId}
                  rows={rows}
                  selection={selection}
                  onSelectionChange={setSelection}
                  drafts={drafts}
                  pendingIds={locked ? rows.map((r) => r.id) : pendingIds}
                  handlers={handlersRef.current}
                />
              )}
              {total > VACANCIES_PAGE_SIZE && (
                <Pagination
                  sx={{ mt: 1.5 }}
                  count={Math.ceil(total / VACANCIES_PAGE_SIZE)}
                  page={page + 1}
                  onChange={(_, next) => {
                    setSelection([]);
                    setPage(next - 1);
                  }}
                />
              )}
            </>
          ) : (
            <DataGrid
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              rows={rows}
              columns={columns}
              loading={loading}
              autoHeight
              disableVirtualization
              disableColumnMenu
              disableColumnFilter
              disableSelectionOnClick
              checkboxSelection
              selectionModel={selection}
              onSelectionModelChange={(model) => setSelection(model)}
              pagination
              paginationMode="server"
              rowCount={total}
              page={page}
              pageSize={VACANCIES_PAGE_SIZE}
              rowsPerPageOptions={[VACANCIES_PAGE_SIZE]}
              onPageChange={(next) => {
                setSelection([]);
                setPage(next);
              }}
              getRowClassName={({ row }) => (situationOf(row) === "no" ? "row-no" : warnsNotValidated(row) ? "row-nv" : "")}
              sx={{
                bgcolor: "background.paper",
                "& .row-no": { opacity: 0.6 },
                "& .row-nv": { bgcolor: "#fff8e1" },
                "& .warnchip": { color: "#b26a00", fontWeight: 600 },
              }}
            />
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 1 }}>
            <span>
              {pagedPackage ? `${summary.conferred} de ${rows.length} desta página conferidos` : `${summary.conferred} de ${total} conferidos`} ·{" "}
              {plural(summary.notAdvancing, "marcado", "marcados")} para não antecipar
            </span>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={onClose}>
                Fechar
              </Button>
              {/* sem as linhas carregadas o aviso de divisão sairia vazio */}
              <Button variant="contained" onClick={openSend} disabled={locked || loading || pendingIds.length > 0}>
                Enviar para Antecipe
              </Button>
            </Box>
          </Box>
        </>
      )}
      <SendPackageDialog
        open={sendState.open}
        packageId={packageId}
        rows={sendState.rows}
        sending={sending}
        refusalLabel={sendState.refusalLabel}
        onCancel={() => setSendState(CLOSED_SEND)}
        onConfirm={confirmSend}
      />
      {refusalState && (
        <RefusalDialog
          context={packageSummary(refusalState.rows)}
          rows={refusedRows(refusalState.rows)}
          total={isTotalRefusal(refusalState.rows)}
          busy={sending}
          error={refusalState.error}
          onConfirm={confirmRefusal}
          onClose={() => !sending && setRefusalState(null)}
        />
      )}
      {slaState && (
        <SlaJustifyDialog
          requirement={slaState.requirement}
          sla={packageRow?.sla?.stage === slaState.requirement.stage ? packageRow.sla : null}
          context={packageSummary(slaState.rows)}
          targetLabel="Antecipe"
          direction="forward"
          busy={sending}
          error={slaState.error}
          onConfirm={(sla) => doSend({ ...slaState.body, sla }, slaState.rows)}
          onClose={() => !sending && setSlaState(null)}
        />
      )}
      <MergedPackageDialog
        open={Boolean(mergedInto)}
        packageId={packageId}
        mergedIntoId={mergedInto}
        onClose={() => setMergedInto(null)}
        onOpen={(id) => {
          setMergedInto(null);
          onOpenPackage(id);
        }}
      />
    </Box>
  );
}
